import {
    useFormEngineElement,
    useFormEngineElementController,
} from '@form-engine/core-application/hooks/use-element';
import { useFormElementSession } from '@form-engine/core-application/hooks/use-formsession';
import { FormElementSessionState } from '@form-engine/core-application/types/form';
import {
    FileSelectionSizeError,
    requestAttachmentIndexForUploadState,
    requestUserFile,
    uploadAttachmentForUploadState,
    UploadState,
} from '../utils/upload-handling';
import { useCallback, useEffect, useMemo } from 'react';
import { apiName } from '../components/attachments-element';
import { i18n } from '@form-engine/core-application';

export function useElementSessionUploads() {
    const { state: element } = useFormEngineElement();
    if (!element || element.type !== apiName)
        throw new Error('tried to render input with invalid definition');

    const controller = useFormEngineElementController();

    const [session, setSession] = useFormElementSession<
        FormElementSessionState & { uploads?: UploadState[] }
    >();

    const addUpload = useCallback(
        async (bsoCategory = '', accept?: string, maxSize?: number) => {
            const { uploads } = session;

            setSession({ loading: true });

            try {
                let upload = await requestUserFile(
                    bsoCategory,
                    accept,
                    maxSize
                );

                setSession({
                    loading: true,
                    uploads: [...(uploads ?? []), upload],
                    transientError: null,
                });

                upload = await requestAttachmentIndexForUploadState(
                    controller,
                    upload
                );

                setSession({
                    loading: true,
                    uploads: [...(uploads ?? []), upload],
                    transientError: null,
                });

                upload = await uploadAttachmentForUploadState(upload);

                setSession({
                    loading: false,
                    uploads: [...(uploads ?? []), upload],
                    transientError: null,
                });
            } catch (e) {
                if (e instanceof FileSelectionSizeError) {
                    setSession({
                        loading: false,
                        uploads,
                        transientError: i18n.c('validation.maxAttachmentSize', {
                            max: maxSize,
                        }),
                        isTouched: true,
                    });
                    return;
                }

                console.warn(e);
                setSession({
                    loading: false,
                    uploads,
                });

                return;
            }
        },
        [session?.uploads, setSession, controller]
    );

    const removeUpload = useCallback(
        (id: string) => {
            setSession({
                uploads: [...(session?.uploads ?? [])].filter(
                    (upload) => upload.id !== id
                ),
                transientError: null,
            });
        },
        [session?.uploads, setSession]
    );

    // compute form element value when uploads change
    useEffect(() => {
        // ignore effect when uploads aren't initialized yet
        if (!session?.uploads) {
            return;
        }
        setSession({
            isTouched: true,
            value: JSON.stringify(
                [...(session?.uploads ?? [])]
                    .filter(({ loadState }) => loadState === 'uploaded')
                    .map(({ attachmentIndex }) => attachmentIndex!.attachmentId)
            ),
        });
    }, [session?.uploads, setSession]);

    const categories = useMemo(() => {
        return (element.listSettings ?? []).filter(
            ({ validationMaxAttachments, bsoCategory = '' }) => {
                const max = Number(validationMaxAttachments ?? 1);
                return (
                    max < 1 ||
                    (session?.uploads || []).filter(
                        (u) => u.bsoCategory === bsoCategory
                    ).length < max
                );
            }
        ) as Exclude<typeof element.listSettings, undefined>[number][];
    }, [session?.uploads, element.listSettings]);

    return {
        addUpload,
        removeUpload,
        uploads: session?.uploads ?? [],
        categories,
    };
}
